import { PhotoRecord } from '../types/domain';
import { PolarstepsTrip, PolarstepsLocationPoint, PolarstepsStep } from '../types/polarsteps';

function createSvgThumbnail(title: string, category: string, bg1: string, bg2: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${bg1}" />
        <stop offset="100%" stop-color="${bg2}" />
      </linearGradient>
    </defs>
    <rect width="400" height="400" fill="url(#grad)" />
    <circle cx="200" cy="180" r="70" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" stroke-width="4" />
    <circle cx="200" cy="180" r="30" fill="rgba(255,255,255,0.3)" />
    <text x="200" y="290" font-family="system-ui, sans-serif" font-size="20" font-weight="bold" fill="#ffffff" text-anchor="middle">${title}</text>
    <text x="200" y="320" font-family="system-ui, sans-serif" font-size="14" font-weight="600" fill="rgba(255,255,255,0.8)" text-anchor="middle">${category}</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function generateSampleTrip(): {
  trip: PolarstepsTrip;
  photos: PhotoRecord[];
  locations: PolarstepsLocationPoint[];
  steps: PolarstepsStep[];
} {
  const baseTime = 1718000000; // June 2024

  const steps: PolarstepsStep[] = [
    {
      id: 101,
      name: 'Funchal Old Town',
      location: { name: 'Funchal Old Town', lat: 32.648, lon: -16.908 },
      start_time: baseTime,
      end_time: baseTime + 86400 * 2,
    },
    {
      id: 102,
      name: 'Pico do Arieiro',
      location: { name: 'Pico do Arieiro', lat: 32.735, lon: -16.929 },
      start_time: baseTime + 86400 * 2 + 3600,
      end_time: baseTime + 86400 * 3,
    },
    {
      id: 103,
      name: 'Ponta de São Lourenço',
      location: { name: 'Ponta de São Lourenço', lat: 32.743, lon: -16.702 },
      start_time: baseTime + 86400 * 3 + 3600,
      end_time: baseTime + 86400 * 4,
    },
    {
      id: 104,
      name: 'Porto Moniz Pools',
      location: { name: 'Porto Moniz Pools', lat: 32.867, lon: -17.168 },
      start_time: baseTime + 86400 * 4 + 3600,
      end_time: baseTime + 86400 * 5,
    },
    {
      id: 105,
      name: 'Cabo Girão Skywalk',
      location: { name: 'Cabo Girão Skywalk', lat: 32.651, lon: -17.004 },
      start_time: baseTime + 86400 * 5 + 3600,
      end_time: baseTime + 86400 * 6,
    },
  ];

  const locations: PolarstepsLocationPoint[] = [];
  const totalDays = 6;
  const numRoutePoints = 40;

  for (let i = 0; i <= numRoutePoints; i++) {
    const ratio = i / numRoutePoints;
    const time = Math.round(baseTime + ratio * totalDays * 86400);

    // Approximate scenic route across Madeira
    let lat: number;
    let lon: number;

    if (ratio < 0.25) {
      lat = 32.648 + (32.735 - 32.648) * (ratio / 0.25);
      lon = -16.908 + (-16.929 - -16.908) * (ratio / 0.25);
    } else if (ratio < 0.5) {
      const sub = (ratio - 0.25) / 0.25;
      lat = 32.735 + (32.743 - 32.735) * sub;
      lon = -16.929 + (-16.702 - -16.929) * sub;
    } else if (ratio < 0.75) {
      const sub = (ratio - 0.5) / 0.25;
      lat = 32.743 + (32.867 - 32.743) * sub;
      lon = -16.702 + (-17.168 - -16.702) * sub;
    } else {
      const sub = (ratio - 0.75) / 0.25;
      lat = 32.867 + (32.651 - 32.867) * sub;
      lon = -17.168 + (-17.004 - -17.168) * sub;
    }

    // Add tiny realistic jitter
    const jitterLat = (Math.sin(i * 1.5) * 0.003);
    const jitterLon = (Math.cos(i * 1.5) * 0.003);

    locations.push({
      time,
      lat: Number((lat + jitterLat).toFixed(5)),
      lon: Number((lon + jitterLon).toFixed(5)),
    });
  }

  const photoTemplates = [
    { name: 'Mercado dos Lavradores', cat: 'Cibo', step: 'Funchal Old Town', bg1: '#8b5cf6', bg2: '#ec4899', lat: 32.6485, lon: -16.904, dayOffset: 0.5 },
    { name: 'Fortaleza de São Tiago', cat: 'Architettura', step: 'Funchal Old Town', bg1: '#3b82f6', bg2: '#06b6d4', lat: 32.6468, lon: -16.899, dayOffset: 0.8 },
    { name: 'Selfie sul lungomare', cat: 'Con me', step: 'Funchal Old Town', bg1: '#f43f5e', bg2: '#fb7185', lat: 32.647, lon: -16.906, dayOffset: 1.1 },
    { name: 'Azulejos tradizionali', cat: 'Dettagli', step: 'Funchal Old Town', bg1: '#10b981', bg2: '#34d399', lat: 32.650, lon: -16.909, dayOffset: 1.4 },
    { name: 'Tramonto sul porto', cat: 'Panorami', step: 'Funchal Old Town', bg1: '#f59e0b', bg2: '#ef4444', lat: 32.645, lon: -16.912, dayOffset: 1.9 },
    
    { name: 'Alba sopra le nuvole', cat: 'Panorami', step: 'Pico do Arieiro', bg1: '#0ea5e9', bg2: '#38bdf8', lat: 32.7355, lon: -16.928, dayOffset: 2.1 },
    { name: 'Sentiero del crinale', cat: 'Panorami', step: 'Pico do Arieiro', bg1: '#0d9488', bg2: '#14b8a6', lat: 32.738, lon: -16.932, dayOffset: 2.3 },
    { name: 'Foto di vetta a 1818m', cat: 'Con me', step: 'Pico do Arieiro', bg1: '#f43f5e', bg2: '#fda4af', lat: 32.736, lon: -16.927, dayOffset: 2.5 },
    { name: 'Fiori alpini endemici', cat: 'Dettagli', step: 'Pico do Arieiro', bg1: '#10b981', bg2: '#6ee7b7', lat: 32.734, lon: -16.929, dayOffset: 2.7 },

    { name: 'Scogliera vulcanica', cat: 'Panorami', step: 'Ponta de São Lourenço', bg1: '#d97706', bg2: '#f59e0b', lat: 32.742, lon: -16.705, dayOffset: 3.2 },
    { name: 'Onde oceaniche', cat: 'Panorami', step: 'Ponta de São Lourenço', bg1: '#0284c7', bg2: '#0ea5e9', lat: 32.744, lon: -16.699, dayOffset: 3.4 },
    { name: 'Picnic sulla penisola', cat: 'Cibo', step: 'Ponta de São Lourenço', bg1: '#8b5cf6', bg2: '#a855f7', lat: 32.741, lon: -16.708, dayOffset: 3.6 },
    { name: 'Noi al faro', cat: 'Con me', step: 'Ponta de São Lourenço', bg1: '#f43f5e', bg2: '#fb7185', lat: 32.743, lon: -16.700, dayOffset: 3.8 },

    { name: 'Piscine naturali laviche', cat: 'Panorami', step: 'Porto Moniz Pools', bg1: '#06b6d4', bg2: '#22d3ee', lat: 32.868, lon: -17.167, dayOffset: 4.2 },
    { name: 'Bagno nell oceano', cat: 'Con me', step: 'Porto Moniz Pools', bg1: '#f43f5e', bg2: '#f472b6', lat: 32.867, lon: -17.169, dayOffset: 4.4 },
    { name: 'Pranzo con Espada', cat: 'Cibo', step: 'Porto Moniz Pools', bg1: '#f59e0b', bg2: '#fbbf24', lat: 32.866, lon: -17.166, dayOffset: 4.6 },
    { name: 'Riflessi di basalto', cat: 'Dettagli', step: 'Porto Moniz Pools', bg1: '#059669', bg2: '#10b981', lat: 32.869, lon: -17.170, dayOffset: 4.8 },

    { name: 'Passerella di cristallo', cat: 'Architettura', step: 'Cabo Girão Skywalk', bg1: '#6366f1', bg2: '#818cf8', lat: 32.652, lon: -17.003, dayOffset: 5.2 },
    { name: 'Baratro di 580m', cat: 'Panorami', step: 'Cabo Girão Skywalk', bg1: '#0284c7', bg2: '#38bdf8', lat: 32.650, lon: -17.005, dayOffset: 5.4 },
    { name: 'Noi sospesi nel vuoto', cat: 'Con me', step: 'Cabo Girão Skywalk', bg1: '#e11d48', bg2: '#fb7185', lat: 32.651, lon: -17.004, dayOffset: 5.6 },
    { name: 'Brindisi al tramonto', cat: 'Cibo', step: 'Cabo Girão Skywalk', bg1: '#9333ea', bg2: '#c084fc', lat: 32.653, lon: -17.002, dayOffset: 5.8 },
  ];

  const photos: PhotoRecord[] = photoTemplates.map((t, idx) => {
    const timestampUtc = Math.round(baseTime + t.dayOffset * 86400);
    const thumbUrl = createSvgThumbnail(t.name, t.cat, t.bg1, t.bg2);

    return {
      id: `photo-sample-${idx + 1}`,
      fileName: `${t.name.toLowerCase().replace(/\s+/g, '_')}.jpg`,
      category: t.cat,
      dateTimeOriginal: new Date(timestampUtc * 1000).toISOString().replace('T', ' ').slice(0, 19),
      timestampUtc,
      lat: t.lat,
      lon: t.lon,
      matchingConfidence: 'exact',
      stepName: t.step,
      thumbnailKey: thumbUrl,
      previewKey: thumbUrl,
      thumbnailSize: 45000,
      previewSize: 180000,
      exif: {
        make: 'Sony',
        model: 'Alpha 7 IV',
        focalLength: '24mm',
        aperture: 'f/2.8',
        iso: 100,
        shutterSpeed: '1/500s',
      },
    };
  });

  const trip: PolarstepsTrip = {
    id: 1,
    name: 'Madeira Explorer 2024',
    timezone_id: 'Atlantic/Madeira',
    start_date: baseTime,
    end_date: baseTime + totalDays * 86400,
    all_steps: steps,
    locations,
  };

  return { trip, photos, locations, steps };
}
